import { useCallback, useEffect, useRef } from "react";

import { useScript } from "@ui/hooks/useScript";

type AgeProofAvProps = {
  scriptLink: string;
  scriptKey: string;
  onAvFinish: (verificationUuid: string) => void;
};

export const AGEPROOF_INPUT_ID_SELECTOR = "#ageproof-uuid";

export const AgeProofAv = ({ scriptLink, scriptKey, onAvFinish }: AgeProofAvProps) => {
  const ageproofContainerRef = useRef<HTMLDivElement>(null);

  useScript({ scriptSrc: scriptLink, removeOnUnmount: true });

  const observeAgeProofId = useCallback(
    (ageproofIdInput: HTMLInputElement) => {
      const ageproofIdObserver = new MutationObserver(() => {
        const ageproofVerificationId = ageproofIdInput.value;

        if (ageproofVerificationId) {
          onAvFinish(ageproofVerificationId);

          ageproofIdObserver.disconnect();
        }
      });

      ageproofIdObserver.observe(ageproofIdInput, { attributes: true });
    },
    [onAvFinish]
  );

  const checkAgeProofId = useCallback(
    (ageproofIdInput: HTMLInputElement) => {
      const ageproofVerificationId = ageproofIdInput.value;

      if (ageproofVerificationId) {
        onAvFinish(ageproofVerificationId);

        return;
      }

      observeAgeProofId(ageproofIdInput);
    },
    [onAvFinish, observeAgeProofId]
  );

  useEffect(() => {
    const ageproofContainerObserver = new MutationObserver(() => {
      const avVerificationIdInput =
        ageproofContainerRef.current?.querySelector<HTMLInputElement>(AGEPROOF_INPUT_ID_SELECTOR);

      if (avVerificationIdInput?.value) {
        checkAgeProofId(avVerificationIdInput);
        ageproofContainerObserver.disconnect();
      }
    });

    if (!ageproofContainerRef.current) return;

    ageproofContainerObserver.observe(ageproofContainerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      ageproofContainerObserver.disconnect();
    };
  }, [checkAgeProofId]);

  return <div className="ageproof-cz" data-apikey={scriptKey} ref={ageproofContainerRef} />;
};
